import { useState } from 'preact/hooks'
import { findCoveringHost, normalizeHost } from '@/utils/domainHost'
import { parseValidationErrors } from '@/utils/errorHandling'
import {
  NON_PROSPECT_DOMAINS_MAX_COUNT,
  type InheritedNonProspectDomain,
  type InheritedNonProspectDomainSource,
  type NonProspectDomainsViewDto,
} from '@/types/nonProspectDomains'
import type { ApiResponse } from '@/api/client'

/**
 * How each inherited level is named in the "already applied from …" message. A strict lookup, not a
 * ternary: a source the UI does not know about reads as itself rather than being silently labelled
 * "company" (repo rule — no quiet fallbacks). Module scope so it is allocated once and there is a
 * single place the wording lives.
 */
const LEVEL_NAMES: Record<InheritedNonProspectDomainSource, string> = {
  client_company: 'company',
  managing_company: 'managing company',
}

type SaveNonProspectDomains = (
  domains: string[],
) => Promise<ApiResponse<NonProspectDomainsViewDto> | null>

/** State + handlers NonProspectDomainsPanel renders. Keeps the component presentation-only. */
export interface NonProspectDomainsEditor {
  /** Read-only rows owned by a level above. */
  inherited: InheritedNonProspectDomain[]
  /** This level's editable entries. */
  own: string[]
  adding: boolean
  newDomain: string
  error: string | null
  /** True during ANY mutation, add or remove — every control is disabled while one is in flight. */
  saving: boolean
  /** The entry currently being removed, so only its row shows a spinner. */
  removing: string | null
  atLimit: boolean
  openAddForm: () => void
  closeAddForm: () => void
  setNewDomain: (value: string) => void
  clearError: () => void
  handleAdd: () => Promise<void>
  handleRemove: (domain: string) => Promise<void>
}

/**
 * Local state, client-side validation (mirroring the server normalizer), and the PATCH-per-mutation
 * save orchestration for the ORB-619 non-prospect domain editor. Mirrors useApprovedDomainsEditor,
 * which is the established shape for this kind of list in the codebase.
 *
 * ⚑ `saving` covers removes as well as adds, and both clear in a `finally`. The list is replaced
 * wholesale against a single ETag, so two mutations in flight at once means the second PATCHes a
 * stale ETag and 412s — and a throw mid-save would otherwise leave a row spinning forever.
 */
export function useNonProspectDomainsEditor(
  view: NonProspectDomainsViewDto,
  onSave: SaveNonProspectDomains,
): NonProspectDomainsEditor {
  const own = view.NonProspectDomains
  const inherited = view.InheritedDomains

  const [adding, setAdding] = useState(false)
  const [newDomain, setNewDomain] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [removing, setRemoving] = useState<string | null>(null)

  const atLimit = own.length >= NON_PROSPECT_DOMAINS_MAX_COUNT

  const openAddForm = () => {
    setNewDomain('')
    setError(null)
    setAdding(true)
  }

  const closeAddForm = () => {
    setAdding(false)
    setNewDomain('')
    setError(null)
  }

  const clearError = () => {
    if (error) setError(null)
  }

  // A 422 (invalid entry / over the cap) belongs inline next to the input; the owning hook toasts
  // everything else.
  const applyServerError = (
    res: ApiResponse<NonProspectDomainsViewDto> | null,
    fallback: string,
  ) => {
    if (res && res.status === 422) {
      const messages = parseValidationErrors(res)
      setError(messages[0]?.Message ?? fallback)
    }
  }

  /** Why `host` is already covered, phrased so the user knows where to go and change it. */
  const coverageError = (host: string): string | null => {
    const coveredBy = findCoveringHost(host, [...inherited.map((d) => d.Host), ...own])
    if (!coveredBy) return null

    const inheritedMatch = inherited.find((d) => d.Host === coveredBy)
    if (inheritedMatch) {
      const level = LEVEL_NAMES[inheritedMatch.Source] ?? `“${inheritedMatch.Source}”`
      const named = inheritedMatch.SourceName ? ` (${inheritedMatch.SourceName})` : ''
      return `Already applied from the ${level} list${named}.`
    }

    return coveredBy === host
      ? 'That domain is already on this list.'
      : `Already covered by ${coveredBy} — that entry covers its subdomains.`
  }

  const handleAdd = async () => {
    const host = normalizeHost(newDomain)
    if (!host) {
      setError('Enter a valid domain, e.g. example.com — no wildcards, paths, or spaces.')
      return
    }

    const covered = coverageError(host)
    if (covered) {
      setError(covered)
      return
    }

    if (atLimit) {
      setError(`You can add at most ${NON_PROSPECT_DOMAINS_MAX_COUNT} non-prospect domains.`)
      return
    }

    setSaving(true)
    setError(null)
    try {
      const res = await onSave([...own, host])
      if (!res?.data) {
        applyServerError(res, 'Could not add that domain.')
        return
      }
      // Keep the box open with a cleared input so several can be entered in a row.
      setNewDomain('')
    } finally {
      setSaving(false)
    }
  }

  const handleRemove = async (domain: string) => {
    setRemoving(domain)
    setSaving(true)
    try {
      const res = await onSave(own.filter((d) => d !== domain))
      // A remove that 422s is unexpected — the list was already valid.
      if (res && res.status === 422) applyServerError(res, 'Could not remove that domain.')
    } finally {
      setRemoving(null)
      setSaving(false)
    }
  }

  return {
    inherited,
    own,
    adding,
    newDomain,
    error,
    saving,
    removing,
    atLimit,
    openAddForm,
    closeAddForm,
    setNewDomain,
    clearError,
    handleAdd,
    handleRemove,
  }
}
